import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {Link, Outlet, useLocation, useParams} from "react-router-dom";
import {useAtom, useAtomValue} from "jotai";
import {Loadable} from "jotai/vanilla/utils/loadable";
import {Box, Button, Divider, Stack, Tab, Tabs, Typography} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import {AppLayout} from "../AppLayout";
import {MenuItem} from "../../types/menuItem";
import {AuthorizationGuard} from "../AuthorizationGuard";
import {User, UserGlobalRole} from "../../types/domain/user";
import {activeUserLoadable} from "../../store/activeUserStore";
import {getUserFullName} from "../../utils/userUtils";
import {impersonationAtom} from "../../store/impersonationStore";
import {ResourceException} from "../../types/resourceError";
import {authenticatedUserLoadable} from "../../store/authenticatedUserStore";
import {ImpersonateIcon} from "../icons/ImpersonateIcon";
import {UserIcon} from "../icons/UserIcon";
import {useResourceValueOrDefault} from "../../hooks/resourceHooks";
import {useRouterParamsStoreSync} from "../../store/routerStore";

export const UserPage: React.FC = () => {
    useRouterParamsStoreSync();

    const {t} = useTranslation();
    const userResource = useAtomValue(activeUserLoadable);
    const user = useResourceValueOrDefault(userResource, undefined);
    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const authenticatedUser = useResourceValueOrDefault(authenticatedUserResource, undefined);
    const [impersonation, setImpersonation] = useAtom(impersonationAtom);

    return (
        <AuthorizationGuard permitAnyOfGlobalRoles={[UserGlobalRole.ADMIN, UserGlobalRole.CS, UserGlobalRole.HYDROSAT]}>
            <AppLayout sidePanel={<UserPagePanel />}>
                <Stack direction="column" paddingX="3rem" paddingTop="3rem" paddingBottom="0.5rem" gap="2.25rem">
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap="1.125rem">
                            {user && (
                                <UserIcon
                                    sx={{
                                        stroke: "#404DB8",
                                        width: "1.625rem",
                                        height: "1.625rem",
                                    }}
                                    color="primary"
                                />
                            )}
                            {userResource.state === "hasError" && (
                                <ErrorIcon
                                    sx={{
                                        height: 36,
                                        width: 36,
                                    }}
                                    color="error"
                                />
                            )}
                            <Typography fontSize={30} lineHeight={1.27} fontWeight={700}>
                                {renderTitle(userResource, t)}
                            </Typography>
                        </Stack>
                        {user && impersonation === undefined && user.id !== authenticatedUser?.id && (
                            <Button
                                startIcon={
                                    <ImpersonateIcon
                                        sx={{
                                            stroke: "#404DB8",
                                            width: "1.25rem",
                                            height: "1.25rem",
                                        }}
                                    />
                                }
                                onClick={() => setImpersonation(user.id)}
                            >
                                Impersonate
                            </Button>
                        )}
                    </Stack>
                    {user && (
                        <Stack direction="column">
                            <Outlet />
                        </Stack>
                    )}
                    {userResource.state === "hasError" && <Typography>Please, retry later.</Typography>}
                </Stack>
            </AppLayout>
        </AuthorizationGuard>
    );
};

const UserPagePanel: React.FC = () => {
    const {t} = useTranslation();
    const {userId} = useParams();
    const pathname = useLocation().pathname;
    const userState = useAtomValue(activeUserLoadable);
    const user = userState.state === "hasData" && userState.data !== undefined ? userState.data : undefined;

    return (
        <Stack direction="column" sx={{height: "calc(100% - 2.25rem)", padding: "1.125rem"}} overflow="hidden">
            <Typography textTransform="uppercase" sx={{color: "#737990", my: "0.5rem"}}>
                {t("user_page.title")}
            </Typography>
            <Divider />
            {userId && user && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "0.5rem",
                            mt: "0.5rem",
                            mb: "0.75rem",
                        }}
                    >
                        <UserIcon color="primary" />
                        <Typography>{getUserFullName(user)}</Typography>
                    </Box>
                    <Tabs
                        value={pathname}
                        orientation="vertical"
                        textColor="inherit"
                        variant="fullWidth"
                        TabIndicatorProps={{
                            sx: {
                                width: 0,
                            },
                        }}
                        sx={{
                            minHeight: "auto",
                            color: "#000000A0",
                            "& a:hover": {backgroundColor: "#eceff5"},
                            "& .Mui-selected": {backgroundColor: "#f3f5fa"},
                        }}
                    >
                        {menuItems(userId).map((item) => (
                            <Tab
                                color="primary"
                                key={item.href}
                                label={t(item.i18nLabel)}
                                icon={item.icon}
                                iconPosition="start"
                                value={item.href}
                                id={item.href}
                                component={Link}
                                to={item.href}
                                sx={{
                                    minHeight: "2.25rem",
                                    height: "2.5rem",
                                    letterSpacing: ".04em",
                                    justifyContent: "left",

                                    whiteSpace: "nowrap",
                                    fontSize: "0.875rem",
                                    opacity: 1,
                                    px: "2rem",
                                    py: "0.5rem",
                                }}
                            />
                        ))}
                    </Tabs>
                </>
            )}
        </Stack>
    );
};

const menuItems = (userId: string): MenuItem[] => [
    {
        i18nLabel: "user_page.menu.user_details",
        href: `/user/${userId}`,
    },
    {
        i18nLabel: "user_page.menu.companies",
        href: `/user/${userId}/companies`,
    },
    {
        i18nLabel: "user_page.menu.orders",
        href: `/user/${userId}/orders`,
    },
    {
        i18nLabel: "user_page.menu.farms",
        href: `/user/${userId}/farms`,
    },
];

const renderTitle = (
    userState: Loadable<Promise<User | undefined>>,
    t: TFunction<"translation", undefined>,
): React.ReactNode => {
    if (userState.state === "hasData" && userState.data) {
        return getUserFullName(userState.data);
    } else if (userState.state === "hasData" && userState.data === undefined) {
        return t("user_page.panel.title_user_not_selected");
    } else if (userState.state === "hasError") {
        return (userState.error as ResourceException).resourceError.message;
    } else {
        return null;
    }
};
